import { NumberField } from "@base-ui/react/number-field";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Tooltip,
  Typography,
} from "@mui/material";
import { isNil } from "lodash";

interface Props {
  value: number | null;
  inputLabel: string;
  onChange: (value: number | null) => void;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  tooltip?: string;
  disabled?: boolean;
  helperText?: React.ReactNode;
  error?: boolean;
  min?: number;
  max?: number;
  step?: number;
  integerOnly?: boolean;
  maxDecimals?: number;
}

export function NumberInput(props: Props) {
  const {
    startAdornment = null,
    endAdornment = null,
    value,
    inputLabel,
    onChange,
    tooltip = null,
    disabled = false,
    helperText = null,
    error = false,
    min = undefined,
    max = undefined,
    step = undefined,
    integerOnly = false,
    maxDecimals = undefined,
  } = props;

  const inputId = `number-input-${inputLabel}`;
  const helperTextId = `${inputId}-helper-text`;

  const maximumFractionDigits = integerOnly ? 0 : maxDecimals;
  const format: Intl.NumberFormatOptions | undefined =
    maximumFractionDigits === undefined ? undefined : { maximumFractionDigits };

  return (
    <NumberField.Root
      value={value}
      onValueChange={(newValue) => onChange(newValue)}
      disabled={disabled}
      min={min}
      max={max}
      step={step}
      format={format}
      render={(rootProps, state) => (
        <FormControl
          fullWidth
          ref={rootProps.ref}
          disabled={state.disabled}
          error={error}
          variant="outlined"
        >
          {rootProps.children}
        </FormControl>
      )}
    >
      <InputLabel htmlFor={inputId}>{inputLabel}</InputLabel>
      <NumberField.Input
        id={inputId}
        render={(inputProps, state) => {
          const { ref: inputElementRef, ...restInputProps } = inputProps;
          return (
            <Tooltip
              title={isNil(tooltip) ? "" : <Typography>{tooltip}</Typography>}
              arrow
              placement="right"
            >
              <OutlinedInput
                label={inputLabel}
                value={state.inputValue}
                inputRef={inputElementRef}
                onBlur={inputProps.onBlur}
                onChange={inputProps.onChange}
                onKeyDown={inputProps.onKeyDown}
                onKeyUp={inputProps.onKeyUp}
                onFocus={inputProps.onFocus}
                startAdornment={startAdornment ?? null}
                endAdornment={endAdornment ?? null}
                slotProps={{ input: restInputProps }}
                aria-describedby={isNil(helperText) ? undefined : helperTextId}
              />
            </Tooltip>
          );
        }}
      />
      {!isNil(helperText) && (
        <FormHelperText id={helperTextId}>{helperText}</FormHelperText>
      )}
    </NumberField.Root>
  );
}
